import { useEffect, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { suggestSpecies } from "../lib/api";

export const SpeciesSearchBox = ({ onSearch, loading, initial = "" }) => {
  const [q, setQ] = useState(initial);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const skip = useRef(false);
  const timer = useRef(null);

  const focused = useRef(false);
  useEffect(() => { if (initial !== q) { skip.current = true; setQ(initial); } }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    if (q.trim().length < 2) { setItems([]); return; }
    const term = q.trim();
    timer.current = setTimeout(() => suggestSpecies(term).then((r) => { if (timer.current) { setItems(r); setOpen(focused.current); } }).catch(() => setItems([])), 300);
    return () => { clearTimeout(timer.current); timer.current = null; };
  }, [q]);

  const go = (name) => { clearTimeout(timer.current); timer.current = null; skip.current = name !== q; setQ(name); setOpen(false); setItems([]); if (name.trim()) onSearch(name.trim()); };

  return (
    <div className="relative max-w-2xl" data-testid="species-search-box">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); go(q); }}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="species-search-input" className="pl-9 font-mono text-sm" placeholder='e.g. "Actias luna", "Biston betularia" or "luna moth"' value={q}
            onChange={(e) => setQ(e.target.value)} onFocus={() => { focused.current = true; if (items.length) setOpen(true); }} onBlur={() => { focused.current = false; setTimeout(() => setOpen(false), 150); }} />
        </div>
        <Button data-testid="species-search-button" type="submit" size="sm" className="h-9" disabled={loading || q.trim().length < 2}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </Button>
      </form>
      {open && items.length > 0 && (
        <ul data-testid="species-suggestions" className="absolute z-20 mt-1 w-full rounded border bg-card shadow-lg max-h-80 overflow-auto">
          {items.map((s, i) => (
            <li key={s.name}>
              <button type="button" data-testid={`species-suggestion-${i}`} onMouseDown={() => go(s.name)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent">
                {s.photo ? <img src={s.photo} alt="" className="h-8 w-8 rounded-sm object-cover" /> : <div className="h-8 w-8 rounded-sm bg-secondary" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm italic truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.common_name || "—"} · {s.rank}</div>
                </div>
                <div className="flex gap-1">{s.sources.map((x) => <span key={x} className="text-[10px] font-mono rounded bg-secondary px-1.5 py-0.5">{x}</span>)}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
